import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../components/api';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    try {
      var saved = localStorage.getItem('auth');
      if (!saved) return {};
      var parsed = JSON.parse(saved);
      // Accept both old format (with token) and new format (UI hints only)
      // Old format token is used by api.js interceptor as Authorization header fallback
      if (parsed.username && parsed.roles) return parsed;
      return {};
    } catch(e) { return {}; }
  });

  // Refresh permissions + screens from DB (used on mount + periodic fallback)
  const refreshPerms = useCallback(function() {
    if (!auth?.username) return;
    // Skip for saadhak (patient) logins — they have no User record
    if (auth.roles && auth.roles.length === 1 && auth.roles[0] === 'saadhak') return;
    api.get(process.env.REACT_APP_URL + '/users/session-check')
      .then(function(r) {
        if (r.data && r.data.permissions) {
          setAuth(function(prev) {
            return Object.assign({}, prev, {
              permissions: r.data.permissions,
              allowedScreens: r.data.screens || [],
              roles: r.data.roles || prev.roles,
            });
          });
        }
      })
      .catch(function() { /* 401 handled by api interceptor */ });
  }, [auth?.username, auth?.roles]); // eslint-disable-line react-hooks/exhaustive-deps

  // On app mount: refresh once
  const permRefreshed = useRef(false);
  useEffect(() => {
    if (!auth?.username || permRefreshed.current) return;
    permRefreshed.current = true;
    refreshPerms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic fallback: re-check every 5 minutes in case SSE event was missed
  useEffect(() => {
    if (!auth?.username) return;
    if (auth.roles && auth.roles.length === 1 && auth.roles[0] === 'saadhak') return;
    var interval = setInterval(refreshPerms, 5 * 60 * 1000);
    return function() { clearInterval(interval); };
  }, [auth?.username, refreshPerms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync to localStorage whenever auth changes
  useEffect(() => {
    if (auth && auth.username && auth.roles) {
      localStorage.setItem('auth', JSON.stringify(auth));
    } else {
      // Logged out — clear all storages
      localStorage.removeItem('auth');
      sessionStorage.removeItem('auth');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('lu');
      sessionStorage.removeItem('saadhakProfile');
    }
  }, [auth]);

  // Expose clearAuth for logout from anywhere
  var clearAuth = useCallback(function() {
    setAuth({});
    localStorage.removeItem('auth');
    sessionStorage.removeItem('auth');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('lu');
    sessionStorage.removeItem('saadhakProfile');
  }, []);

  return (
    <AuthContext.Provider value={{ auth, setAuth, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
